// "Page actions" editor — rendered inside the Page properties panel.
// Lists named actions, lets the author add/edit/delete them, and embeds the
// recursive StepList for each action's steps.

import { useEffect, useState } from 'react'
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { FIELD_LABEL, INPUT } from '../blockEditors'
import type { PageAction, PageInput } from '../types'
import { StepList } from './StepEditors'
import { getIntegrationDesign } from '@/api/integration'

interface Props {
  actions: PageAction[]
  inputs: PageInput[]
  /** Project folder — enables the IOFlow API picker on call_api steps. */
  project?: string
  /** The project's transactions — the object picker of the record steps. */
  transactions?: { obj_name: string; title: string }[]
  /** This page's data-source ids — the refresh pickers. */
  dataSources?: string[]
  onChange: (next: PageAction[]) => void
}

let _idCounter = 0
function newId(): string {
  _idCounter += 1
  return `act_${Date.now().toString(36)}_${_idCounter}`
}

function makeAction(existingNames: string[]): PageAction {
  let i = existingNames.length + 1
  let name = `action_${i}`
  while (existingNames.includes(name)) { i += 1; name = `action_${i}` }
  return { id: newId(), name, description: '', steps: [] }
}

export default function ActionEditor({ actions, inputs, project, transactions, dataSources, onChange }: Props) {
  // Active IOFlow APIs from the project's Integration design (Advanced →
  // Events & APIs) — offered as routing targets on call_api steps.
  const [ioflowApis, setIoflowApis] = useState<string[]>([])
  useEffect(() => {
    if (!project) return
    let alive = true
    getIntegrationDesign(project)
      .then((d) => {
        if (alive) setIoflowApis((d.apis || []).filter((a) => a.active !== false).map((a) => a.api_name))
      })
      .catch(() => { /* no design yet — picker just stays empty */ })
    return () => { alive = false }
  }, [project])

  const add = () => onChange([...actions, makeAction(actions.map((a) => a.name))])
  const update = (i: number, next: PageAction) => {
    const out = [...actions]; out[i] = next; onChange(out)
  }
  const remove = (i: number) => onChange(actions.filter((_, k) => k !== i))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...FIELD_LABEL, marginBottom: 0 }}>Actions ({actions.length})</label>
        <button
          onClick={(e) => { e.stopPropagation(); add() }}
          className="btn btn-ghost btn-sm"
          title="Add action"
        ><Plus size={13} /> Add</button>
      </div>
      {actions.length === 0 && (
        <div style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          Define a named action (e.g. <code>login</code>, <code>save</code>) and reference it from a button's
          <em> On click → Run page action</em>. Each action runs a sequence of steps: validate inputs, call an API,
          show a message, focus an input on error, etc.
        </div>
      )}
      {actions.map((a, i) => {
        const dup = actions.findIndex((b) => b.name === a.name) !== i
        return (
          <ActionCard
            key={a.id}
            action={a}
            duplicateName={dup}
            inputs={inputs}
            ioflowApis={ioflowApis}
            txnCtx={{ project, transactions, dataSources }}
            onChange={(next) => update(i, next)}
            onRemove={() => remove(i)}
          />
        )
      })}
    </div>
  )
}

function ActionCard({
  action, inputs, ioflowApis, txnCtx, duplicateName, onChange, onRemove,
}: {
  action: PageAction
  inputs: PageInput[]
  ioflowApis: string[]
  txnCtx: { project?: string; transactions?: { obj_name: string; title: string }[]; dataSources?: string[] }
  duplicateName: boolean
  onChange: (next: PageAction) => void
  onRemove: () => void
}) {
  const [collapsed, setCollapsed] = useState(true)
  return (
    <div style={{
      padding: 10, borderRadius: 8,
      background: 'var(--color-bg)',
      border: duplicateName ? '1px solid #ef5350' : '1px solid var(--color-border)',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer', userSelect: 'none',
      }} onClick={() => setCollapsed(!collapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          <span style={{
            fontFamily: 'monospace', fontSize: '0.9125rem', fontWeight: 600,
            color: duplicateName ? '#ef5350' : 'var(--color-text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{action.name || '(unnamed)'}</span>
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            · {action.steps.length} step{action.steps.length === 1 ? '' : 's'}
          </span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="btn btn-ghost btn-sm"
          style={{ padding: 2, color: 'var(--color-error)' }}
          title="Delete action"
        ><Trash2 size={13} /></button>
      </div>
      {duplicateName && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)' }}>
          Duplicate name — actions must have unique names.
        </div>
      )}
      {!collapsed && (
        <>
          <div>
            <label style={FIELD_LABEL}>Name</label>
            <input
              value={action.name}
              onChange={(e) => onChange({
                ...action,
                name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'),
              })}
              placeholder="action_name"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Description</label>
            <input
              value={action.description || ''}
              onChange={(e) => onChange({ ...action, description: e.target.value })}
              placeholder="What this action does (optional)"
              style={INPUT}
            />
          </div>
          <StepList
            label="Steps"
            steps={action.steps}
            onChange={(next) => onChange({ ...action, steps: next })}
            ctx={{ inputs, ioflowApis, ...txnCtx }}
          />
        </>
      )}
    </div>
  )
}
